import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';
import { Alignment, FlexDirections, Item, MultiSelectList } from 'tacho-ui';
import { useIntl } from 'react-intl';

interface SetFilterProps {
    comparator?: (valueA: string, valueB: string) => 1 | 0 | -1;
}
export const SetFilter = forwardRef((props: SetFilterProps & IFilterParams, ref) => {
    const { formatMessage } = useIntl();
    const [initialData, setInitialData] = useState<Array<Item>>([]);
    const [selectedData, setSelectedData] = useState<Array<string>>(initialData.map(item => item.id));

    const sortBy = (a1: any, a2: any) => {
        return a1.label.localeCompare(a2.label);
    };
    useEffect(() => {
        let array: Array<Item> = [];
        props.api.forEachLeafNode(node => {
            const value = props.valueGetter({
                api: props.api,
                colDef: props.colDef,
                column: props.column,
                context: props.context,
                data: node.data,
                getValue: (field: string) => node.data?.[field],
                node: node,
            });
            array.push({
                id: value || '',
                label: value || '',
                checked: true,
            });
        });
        setInitialData(array);
        setSelectedData(array.map(item => item.id));
    }, []);

    const mapDataToItem = (allItems: Array<any>, selectedItems: Array<string>): Array<Item> => {
        let items = allItems;
        let isEmpty = false;
        items = items.map((item, index) => {
            if (item.id === '' && !isEmpty) {
                isEmpty = true;
                return {
                    id: item.id,
                    label: formatMessage({ id: 'defaultEmpty' }),
                    checked: selectedItems.includes(item.id || ''),
                };
            }

            return {
                id: item.id,
                label: item.label,
                checked: selectedItems.includes(item.id),
            };
        });

        return items
            .filter(item => item.label !== '')
            .reduce((unique, o) => {
                if (!unique.some((obj: any) => obj.label === o.label)) {
                    unique.push(o);
                }
                return unique;
            }, [])
            .sort((a: any, b: any) => (props.comparator ? props.comparator(a.label, b.label) : sortBy(a, b)));
    };
    const handleItemsChange = (items: Array<Item>) => setSelectedData(items.filter(item => item.checked).map(item => item.id));
    useImperativeHandle(ref, () => {
        return {
            doesFilterPass(params: IDoesFilterPassParams) {
                const { api, colDef, column, context } = props;
                const { node } = params;

                // make sure each word passes separately, ie search for firstname, lastname
                let passed = true;
                if (selectedData) {
                    const value = props.valueGetter({
                        api,
                        colDef,
                        column,
                        context,
                        data: node.data,
                        getValue: (field: string) => node.data?.[field],
                        node,
                    });
                    if (!selectedData.includes(value || '')) {
                        passed = false;
                    }
                }

                return passed;
            },

            isFilterActive() {
                return selectedData.length !== initialData.length;
            },

            // this example isn't using getModel() and setModel(),
            // so safe to just leave these empty. don't do this in your code!!!
            getModel() {
                if (!this.isFilterActive()) {
                    return { value: initialData.map(item => item.id) };
                }
                return { value: selectedData };
            },

            setModel(model: any) {
                setTimeout(() => {
                    if (model !== null && model !== undefined && model.value) {
                        setSelectedData(model.value);
                    } else if (model === undefined) {
                        let array: Array<Item> = [];
                        props.api.forEachLeafNode(node => {
                            const value = props.valueGetter({
                                api: props.api,
                                colDef: props.colDef,
                                column: props.column,
                                context: props.context,
                                data: node.data,
                                getValue: (field: string) => node.data?.[field],
                                node: node,
                            });
                            array.push({
                                id: value || '',
                                label: value || '',
                                checked: initialData.map(item => item.id).includes(value),
                            });
                        });
                        setInitialData(array);
                        const elementsToAdd = array
                            .filter(item => !item.checked && !selectedData.includes(item.label))
                            .map(item => item.id);
                        setSelectedData([...selectedData, ...elementsToAdd]);
                    } else {
                        setSelectedData(initialData.map(item => item.id));
                    }
                }, 1);
            },
        };
    });

    useEffect(() => {
        props.filterChangedCallback({});
    }, [selectedData.length]);

    return (
        <MultiSelectList
            showSearchBar
            uncontrolled
            value={mapDataToItem(initialData, selectedData)}
            align={Alignment.flexStart}
            direction={FlexDirections.column}
            onChange={handleItemsChange}
            selectAllText={formatMessage({ id: 'reportsSelectAll' })}
            placeholder={formatMessage({ id: 'reportsSearchPlaceholder' })}
            selectedLabelsText={formatMessage({ id: 'reportsSelected' })}
        />
    );
});
